import { MailCheck } from 'lucide-react'
import { Button } from '@insightai/ui'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { config } from '../../config'
import { mockResendVerification, mockSignIn } from '../../auth/mockAuth'
import { AuthLayout } from './AuthLayout'

/** M00-23 — email verification landing, with resend. */
export function VerifyEmailPage() {
  const [params] = useSearchParams()
  const email = params.get('email')
  const [resent, setResent] = useState(false)
  const [sending, setSending] = useState(false)
  const [continuing, setContinuing] = useState(false)

  /**
   * Prototype mode has no inbox, so without this the unverified fixture account
   * reaches a screen with no way forward. `register: true` is what a just-created
   * account looks like to the mock — nothing to verify — so it resolves to the
   * console rather than back here. The workspace still comes from the fixture; no
   * id is hard-coded on this page.
   */
  async function continueAsVerified() {
    setContinuing(true)
    const { redirectTo } = await mockSignIn({ email: email ?? undefined, register: true })
    window.location.assign(redirectTo)
  }

  return (
    <AuthLayout
      title="Confirm your email"
      subtitle={
        email
          ? `We sent a confirmation link to ${email}.`
          : 'We sent a confirmation link to your work email.'
      }
      footer={
        <Link to="/auth/login" className="text-text-link underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <div className="flex flex-col items-start gap-4">
        <MailCheck aria-hidden className="size-6 text-state-verified" />
        <p className="text-body-sm text-text-secondary">
          Open the link to finish setting up your workspace. It expires in 24 hours.
        </p>

        <Button
          variant="secondary"
          loading={sending}
          onClick={() => {
            setSending(true)
            const request = config.prototypeMode
              ? mockResendVerification()
              : fetch(`${config.apiBaseUrl}/api/auth/resend-verification`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email }),
                }).then(() => undefined)

            void request
              .catch(() => undefined)
              .finally(() => {
                setSending(false)
                setResent(true)
              })
          }}
        >
          Resend the link
        </Button>

        {resent && (
          <p role="status" className="text-caption text-text-secondary">
            Sent again. Check your inbox and spam folder.
          </p>
        )}

        {config.prototypeMode && (
          <div className="flex flex-col gap-2 border-t border-border-subtle pt-4">
            <p className="text-caption text-text-secondary">
              Prototype mode sends no email — confirm it here instead.
            </p>
            <Button
              loading={continuing}
              loadingLabel="Opening your workspace"
              onClick={() => void continueAsVerified()}
            >
              I've confirmed my email
            </Button>
          </div>
        )}
      </div>
    </AuthLayout>
  )
}
